import { useState, useEffect } from "react";
import * as Sentry from "@sentry/react";

import { Medication } from "@types-runway/condition";
import { condition } from "@api/index";

interface MedicationFetchResult {
  medications: Medication[];
  isLoading: boolean;
  error: string | null;
}

const deduplicateMedications = (medications: Medication[]): Medication[] => {
  const seen = new Set<string>();
  return medications.filter((med) => {
    const key = med.id ?? "";
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const medicationsCache = new Map<string, Medication[]>();

export const clearMedicationsCache = () => {
  medicationsCache.clear();
};

const useFetchMedications = (conditionName: string): MedicationFetchResult => {
  const [medications, setMedications] = useState<Medication[]>(
    () => medicationsCache.get(conditionName) ?? []
  );
  const [isLoading, setIsLoading] = useState(() => !medicationsCache.has(conditionName));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!conditionName) {
      setIsLoading(false);
      return;
    }

    if (medicationsCache.has(conditionName)) {
      return;
    }

    const fetchMedications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const encodedName = encodeURIComponent(conditionName);
        const { data } = await condition.conditionGet(`/${encodedName}/medications`);
        const unique = deduplicateMedications(data.medications);
        medicationsCache.set(conditionName, unique);
        setMedications(unique);
      } catch (err) {
        Sentry.captureException(err);
        setError("Failed to load medications. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedications();
  }, [conditionName]);

  return { medications, isLoading, error };
};

export default useFetchMedications;
